import { useState } from "react";
import Dialog from "@/components/Dialog";
import Autocomplete from "@/components/Autocomplete";
import Chip from "@/components/Chip";
import { ChipSet } from "@/components/Chip";
import Button from "@/components/Button";
import AvatarDetails from "@/components/AvatarDetails";
import { useLazyQuery } from "@apollo/client";
import { SEARCH_PEOPLE_QUERY } from "@/components/interactions/queries";

interface Person {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  pgaId?: string;
  profilePhoto?: string;
  phoneNumbers?: {
    phoneNumber: string;
    phoneType: string;
    phoneFormatted: string;
  }[];
  primarySection?: {
    id: string;
    name: string;
  };
}

interface StaffAttendee {
  id: string;
  displayName: string;
  primarySection?: {
    id: string;
    name: string;
  };
  email?: string;
  pgaId?: string;
  phoneNumbers?: {
    phoneNumber: string;
    phoneType: string;
    phoneFormatted: string;
  }[];
}

interface AddEventStaffDialogProps {
  open: boolean;
  onClosed: () => void;
  onSave: (staffAttendees: StaffAttendee[]) => void;
  existingStaffAttendees: StaffAttendee[];
  isSubmitting: boolean;
}

export default function AddEventStaffDialog({
  open,
  onClosed,
  onSave,
  existingStaffAttendees,
  isSubmitting
}: AddEventStaffDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStaff, setSelectedStaff] = useState<StaffAttendee[]>([]);
  const [searchPeople, { data: searchData, loading: searchLoading }] = useLazyQuery(SEARCH_PEOPLE_QUERY);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.length >= 2) {
      searchPeople({ variables: { searchTerm: query } });
    }
  };

  const handleSelectPerson = (person: Person) => {
    // Check if person is already selected
    const isAlreadySelected = selectedStaff.some((staff) => staff.id === person.id);
    if (isAlreadySelected) return;

    // Check if person is already in existing staff
    const isAlreadyExisting = existingStaffAttendees.some((staff) => staff.id === person.id);
    if (isAlreadyExisting) return;

    const newStaff: StaffAttendee = {
      id: person.id,
      displayName: `${person.firstName} ${person.lastName}`,
      primarySection: person.primarySection
        ? {
            id: person.primarySection.id,
            name: person.primarySection.name
          }
        : undefined,
      email: person.email,
      pgaId: person.pgaId,
      phoneNumbers: person.phoneNumbers
    };

    setSelectedStaff((prev) => [...prev, newStaff]);
    setSearchQuery("");
  };

  const handleRemoveStaff = (staffId: string) => {
    setSelectedStaff((prev) => {
      const filtered = prev.filter((staff) => staff.id !== staffId);
      return filtered;
    });
  };

  const handleSave = () => {
    onSave(selectedStaff);
    setSelectedStaff([]);
    setSearchQuery("");
  };

  const handleCancel = () => {
    setSelectedStaff([]);
    setSearchQuery("");
    onClosed();
  };

  const searchResults = searchData?.people?.nodes || [];

  return (
    <Dialog
      open={open}
      onClosed={handleCancel}
      headline="Add Event Staff"
      className="sm:max-w-[600px] p-6 pr-2"
      actions={
        <>
          <Button variant="text" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleSave}
            disabled={isSubmitting || selectedStaff.length === 0}
            icon={isSubmitting ? "progress_activity" : undefined}
            iconClassName={isSubmitting ? "animate-spin w-6 h-6 !block" : undefined}
          >
            Add Staff
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <p className="text-body-medium text-sys-on-surface">
          Add any PGA staff that attended and/or assisted with this event.
        </p>
        <div>
          <Autocomplete
            label="Person"
            data={searchResults}
            query={searchQuery}
            onChangeText={handleSearch}
            onSelect={handleSelectPerson}
            renderItem={(person: Person) => (
              <div className="p-3 hover:bg-sys-surface-container">
                <AvatarDetails
                  title={`${person.firstName} ${person.lastName}`}
                  text={person.pgaId || ""}
                  picture={person.profilePhoto}
                  secondaryText={person.primarySection?.name || ""}
                />
              </div>
            )}
            loadingResults={searchLoading}
          />
        </div>

        {selectedStaff.length > 0 && (
          <div>
            <ChipSet>
              {selectedStaff.map((staff) => (
                <Chip
                  key={`staff-${staff.id}`}
                  label={staff.displayName}
                  removable
                  onRemove={(e) => {
                    e?.preventDefault?.();
                    handleRemoveStaff(staff.id);
                  }}
                />
              ))}
            </ChipSet>
          </div>
        )}
      </div>
    </Dialog>
  );
}
